/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Text } from '@/shared/ui';
import { SegmentedControl } from "@/shared/ui";
import type { SegmentedControlItem } from "@/shared/ui";
import type { HistoryEvent } from "@services/requests/housien-services/ClientInsightsService";
import { AddInteractionForm } from "./interaction-add-form";
import { InteractionEventCard } from "./interaction-event-card";

export type InteractionsFilter = "all" | "interaction" | "visit";

export { AddInteractionForm, InteractionEventCard };

/**
 * Whether an event matches the interactions type filter.
 *
 * @param event - The history event to test.
 * @param filter - The active interactions filter.
 * @returns True when the event should be shown under the current filter.
 */
export function matchesInteractionsFilter(
  event: HistoryEvent,
  filter: InteractionsFilter,
): boolean {
  if (filter === "all") return true;
  return event.event_type === filter;
}

/**
 * Empty state shown when the interactions list has no matching events.
 *
 * @param props - Component props.
 * @param props.label - The empty-state message to display.
 * @returns The rendered empty state.
 */
export function InteractionsEmpty({ label }: { label: string }) {
  return (
    <div
      data-testid="interactions-empty"
      className="flex items-center justify-center h-[120px] text-[var(--text-tertiary)] text-[length:var(--text-body-sm)]"
    >
      <Text component="span">{label}</Text>
    </div>
  );
}

/**
 * Segmented type filter: all / interactions / visits.
 *
 * @param props - Component props.
 * @param props.value - The currently selected filter.
 * @param props.onChange - Callback invoked when the filter changes.
 * @param props.labels - Localized labels per filter value.
 * @returns The rendered segmented control.
 */
export function InteractionsTypeFilter({
  value,
  onChange,
  labels,
}: {
  value: InteractionsFilter;
  onChange: (value: InteractionsFilter) => void;
  labels: Record<InteractionsFilter, string>;
}) {
  const items: SegmentedControlItem<InteractionsFilter>[] = [
    { value: "all", label: labels.all, testId: "filter-all" },
    { value: "interaction", label: labels.interaction, testId: "filter-interaction" },
    { value: "visit", label: labels.visit, testId: "filter-visits" },
  ];
  return (
    <SegmentedControl
      value={value}
      onChange={onChange}
      items={items}
      variant="pill"
      size="sm"
      ariaLabel={labels.all}
      testId="interactions-type-filter"
      className="mb-[var(--space-3)]"
    />
  );
}
